"use client";

import { useState } from "react";
import { BdCalculator } from "solar-icon-react/bd";
import { cn } from "@/lib";

type Scenario = "normal" | "qard" | "rate" | "early" | "step";

type PaymentPeriod = "monthly" | "quarterly" | "semiannual" | "annual";

type ScheduleRow = {
  period: number;
  installment: number;
  principal: number;
  interest: number;
  balance: number;
};

type CalculatorResult = {
  schedule: ScheduleRow[];
  installment: number;
  totalInterest: number;
  totalPayment: number;
  effectiveRate: number;
  remainingDebt: number;
  savedInterest: number;
};

type CalculationOverrides = Partial<{
  scenario: Scenario;
  amount: string;
  rate: string;
  months: string;
  paymentPeriod: PaymentPeriod;
  knownInstallment: string;
  paidInstallments: string;
  growthRate: string;
}>;

type ScenarioPreset = {
  label: string;
  amount: number;
  rate: number;
  months: number;
  knownInstallment?: number;
  paidInstallments?: number;
  growthRate?: number;
};

const scenarios: { key: Scenario; label: string; description: string }[] = [
  {
    key: "normal",
    label: "وام عادی",
    description: "برای وام‌های رایج با قسط مساوی مثل مسکن، خودرو و کالا",
  },
  {
    key: "qard",
    label: "قرض‌الحسنه ۴٪",
    description: "محاسبه کارمزد و قسط وام‌های قرض‌الحسنه",
  },
  {
    key: "rate",
    label: "یافتن نرخ",
    description: "وقتی مبلغ قسط را دارید و می‌خواهید نرخ واقعی را بدانید",
  },
  {
    key: "early",
    label: "تسویه زودتر",
    description: "برآورد مانده بدهی و صرفه‌جویی سود در تسویه زودهنگام",
  },
  {
    key: "step",
    label: "پلکانی",
    description: "محاسبه وام با قسط‌هایی که هر سال افزایش پیدا می‌کنند",
  },
];

const periodConfig: Record<
  PaymentPeriod,
  { label: string; paymentsPerYear: number }
> = {
  monthly: { label: "ماهانه", paymentsPerYear: 12 },
  quarterly: { label: "سه‌ماهه", paymentsPerYear: 4 },
  semiannual: { label: "شش‌ماهه", paymentsPerYear: 2 },
  annual: { label: "سالانه", paymentsPerYear: 1 },
};

const presetsByScenario: Record<Scenario, ScenarioPreset[]> = {
  normal: [
    {
      label: "۱۰۰ میلیون، ۲۳٪، ۳۶ ماه",
      amount: 100_000_000,
      rate: 23,
      months: 36,
    },
    {
      label: "۵۰ میلیون، ۱۸٪، ۲۴ ماه",
      amount: 50_000_000,
      rate: 18,
      months: 24,
    },
    {
      label: "۲۰۰ میلیون، ۲۳٪، ۶۰ ماه",
      amount: 200_000_000,
      rate: 23,
      months: 60,
    },
  ],
  qard: [
    { label: "۵۰ میلیون، ۴٪، ۲۴ ماه", amount: 50_000_000, rate: 4, months: 24 },
    {
      label: "۱۰۰ میلیون، ۴٪، ۳۶ ماه",
      amount: 100_000_000,
      rate: 4,
      months: 36,
    },
    {
      label: "۲۰۰ میلیون، ۴٪، ۶۰ ماه",
      amount: 200_000_000,
      rate: 4,
      months: 60,
    },
  ],
  rate: [
    {
      label: "۱۰۰ میلیون، قسط ۳.۸ میلیون، ۳۶ ماه",
      amount: 100_000_000,
      rate: 0,
      months: 36,
      knownInstallment: 3_870_000,
    },
    {
      label: "۵۰ میلیون، قسط ۲.۵ میلیون، ۲۴ ماه",
      amount: 50_000_000,
      rate: 0,
      months: 24,
      knownInstallment: 2_500_000,
    },
    {
      label: "۲۰۰ میلیون، قسط ۵.۶ میلیون، ۶۰ ماه",
      amount: 200_000_000,
      rate: 0,
      months: 60,
      knownInstallment: 5_600_000,
    },
  ],
  early: [
    {
      label: "۱۰۰ میلیون، ۱۲ قسط پرداخت‌شده",
      amount: 100_000_000,
      rate: 23,
      months: 36,
      paidInstallments: 12,
    },
    {
      label: "۲۰۰ میلیون، ۲۴ قسط پرداخت‌شده",
      amount: 200_000_000,
      rate: 23,
      months: 60,
      paidInstallments: 24,
    },
    {
      label: "۵۰ میلیون، ۶ قسط پرداخت‌شده",
      amount: 50_000_000,
      rate: 18,
      months: 24,
      paidInstallments: 6,
    },
  ],
  step: [
    {
      label: "۱۰۰ میلیون، رشد ۱۰٪، ۳۶ ماه",
      amount: 100_000_000,
      rate: 23,
      months: 36,
      growthRate: 10,
    },
    {
      label: "۲۰۰ میلیون، رشد ۱۵٪، ۶۰ ماه",
      amount: 200_000_000,
      rate: 23,
      months: 60,
      growthRate: 15,
    },
    {
      label: "۵۰ میلیون، رشد ۸٪، ۲۴ ماه",
      amount: 50_000_000,
      rate: 18,
      months: 24,
      growthRate: 8,
    },
  ],
};

const quickRates = [4, 18, 23];
const quickMonths = [12, 24, 36, 60];

const toEnglishDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)));

const parseNumber = (value: string) =>
  Number(
    toEnglishDigits(value)
      .replace(/,/g, "")
      .replace(/[^\d.]/g, ""),
  ) || 0;

const formatNumber = (value: number) =>
  Math.round(value).toLocaleString("fa-IR");

const formatPercent = (value: number) =>
  Number.isFinite(value) ? `${value.toFixed(2).replace(/\.00$/, "")}٪` : "-";

const withRemainingPayableBalance = (schedule: ScheduleRow[]) => {
  let remainingPayable = schedule.reduce(
    (sum, row) => sum + row.installment,
    0,
  );

  return schedule.map((row) => {
    remainingPayable = Math.max(0, remainingPayable - row.installment);

    return {
      ...row,
      balance: remainingPayable,
    };
  });
};

function buildEqualPaymentSchedule(
  principal: number,
  annualRate: number,
  installmentCount: number,
  period: PaymentPeriod,
): ScheduleRow[] {
  const periodicRate = annualRate / 100 / periodConfig[period].paymentsPerYear;
  const installment =
    periodicRate === 0
      ? principal / installmentCount
      : (principal * periodicRate) /
        (1 - Math.pow(1 + periodicRate, -installmentCount));
  let balance = principal;

  const schedule = Array.from({ length: installmentCount }, (_, index) => {
    const interest = balance * periodicRate;
    const principalPaid =
      index === installmentCount - 1 ? balance : installment - interest;
    balance = Math.max(0, balance - principalPaid);

    return {
      period: index + 1,
      installment,
      principal: principalPaid,
      interest,
      balance,
    };
  });

  return withRemainingPayableBalance(schedule);
}

function buildQardSchedule(
  principal: number,
  annualFeeRate: number,
  installmentCount: number,
): ScheduleRow[] {
  const totalFee = (principal * annualFeeRate * (installmentCount + 1)) / 2400;
  const installment = (principal + totalFee) / installmentCount;
  const principalPart = principal / installmentCount;
  const feePart = totalFee / installmentCount;

  const schedule = Array.from({ length: installmentCount }, (_, index) => ({
    period: index + 1,
    installment,
    principal: principalPart,
    interest: feePart,
    balance: Math.max(0, principal - principalPart * (index + 1)),
  }));

  return withRemainingPayableBalance(schedule);
}

function findAnnualRate(
  principal: number,
  installment: number,
  installmentCount: number,
  period: PaymentPeriod,
) {
  let low = 0;
  let high = 1;

  for (let index = 0; index < 80; index += 1) {
    const mid = (low + high) / 2;
    const estimated =
      mid === 0
        ? principal / installmentCount
        : (principal * mid) / (1 - Math.pow(1 + mid, -installmentCount));

    if (estimated > installment) {
      high = mid;
    } else {
      low = mid;
    }
  }

  return ((low + high) / 2) * periodConfig[period].paymentsPerYear * 100;
}

function buildStepSchedule(
  principal: number,
  annualRate: number,
  installmentCount: number,
  annualGrowthRate: number,
) {
  const monthlyRate = annualRate / 1200;
  const growth = annualGrowthRate / 100;
  const denominator = Array.from({ length: installmentCount }).reduce<number>(
    (sum, _, index) => {
      const yearIndex = Math.floor(index / 12);
      return (
        sum +
        Math.pow(1 + growth, yearIndex) / Math.pow(1 + monthlyRate, index + 1)
      );
    },
    0,
  );
  const baseInstallment = denominator
    ? principal / denominator
    : principal / installmentCount;
  let balance = principal;

  const schedule = Array.from({ length: installmentCount }, (_, index) => {
    const yearIndex = Math.floor(index / 12);
    const installment = baseInstallment * Math.pow(1 + growth, yearIndex);
    const interest = balance * monthlyRate;
    const principalPaid =
      index === installmentCount - 1 ? balance : installment - interest;
    balance = Math.max(0, balance - principalPaid);

    return {
      period: index + 1,
      installment,
      principal: principalPaid,
      interest,
      balance,
    };
  });

  return withRemainingPayableBalance(schedule);
}

const LoanInstallmentCalculator = () => {
  const [scenario, setScenario] = useState<Scenario>("normal");
  const [amount, setAmount] = useState("");
  const [rate, setRate] = useState("");
  const [months, setMonths] = useState("");
  const [paymentPeriod, setPaymentPeriod] = useState<PaymentPeriod>("monthly");
  const [knownInstallment, setKnownInstallment] = useState("");
  const [paidInstallments, setPaidInstallments] = useState("");
  const [growthRate, setGrowthRate] = useState("");
  const [result, setResult] = useState<CalculatorResult | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);

  const selectedScenario = scenarios.find((item) => item.key === scenario);
  const scenarioPresets = presetsByScenario[scenario];

  const calculateResult = (
    overrides: CalculationOverrides = {},
  ): CalculatorResult | null => {
    const nextScenario = overrides.scenario ?? scenario;
    const nextPaymentPeriod = overrides.paymentPeriod ?? paymentPeriod;
    const principal = parseNumber(overrides.amount ?? amount);
    const annualRate = parseNumber(overrides.rate ?? rate);
    const count = Math.max(
      1,
      Math.floor(parseNumber(overrides.months ?? months)),
    );
    const paidCount = Math.min(
      count,
      Math.max(
        0,
        Math.floor(parseNumber(overrides.paidInstallments ?? paidInstallments)),
      ),
    );

    if (!principal || !count) return null;

    if (nextScenario === "qard") {
      const schedule = buildQardSchedule(principal, annualRate || 4, count);
      const totalPayment = schedule.reduce(
        (sum, row) => sum + row.installment,
        0,
      );

      return {
        schedule,
        installment: schedule[0]?.installment || 0,
        totalInterest: totalPayment - principal,
        totalPayment,
        effectiveRate: annualRate || 4,
        remainingDebt: 0,
        savedInterest: 0,
      };
    }

    if (nextScenario === "rate") {
      const installment = parseNumber(
        overrides.knownInstallment ?? knownInstallment,
      );
      if (!installment) return null;

      const effectiveRate = findAnnualRate(
        principal,
        installment,
        count,
        nextPaymentPeriod,
      );
      const schedule = buildEqualPaymentSchedule(
        principal,
        effectiveRate,
        count,
        nextPaymentPeriod,
      );
      const totalPayment = installment * count;

      return {
        schedule,
        installment,
        totalInterest: totalPayment - principal,
        totalPayment,
        effectiveRate,
        remainingDebt: 0,
        savedInterest: 0,
      };
    }

    if (nextScenario === "step") {
      const schedule = buildStepSchedule(
        principal,
        annualRate,
        count,
        parseNumber(overrides.growthRate ?? growthRate),
      );
      const totalPayment = schedule.reduce(
        (sum, row) => sum + row.installment,
        0,
      );

      return {
        schedule,
        installment: schedule[0]?.installment || 0,
        totalInterest: totalPayment - principal,
        totalPayment,
        effectiveRate: (Math.pow(1 + annualRate / 1200, 12) - 1) * 100,
        remainingDebt: 0,
        savedInterest: 0,
      };
    }

    const schedule = buildEqualPaymentSchedule(
      principal,
      annualRate,
      count,
      nextPaymentPeriod,
    );
    const totalPayment = schedule.reduce(
      (sum, row) => sum + row.installment,
      0,
    );
    const remainingDebt =
      nextScenario === "early"
        ? (schedule[paidCount - 1]?.balance ?? principal)
        : 0;
    const savedInterest =
      nextScenario === "early"
        ? schedule.slice(paidCount).reduce((sum, row) => sum + row.interest, 0)
        : 0;

    return {
      schedule,
      installment: schedule[0]?.installment || 0,
      totalInterest: totalPayment - principal,
      totalPayment,
      effectiveRate:
        (Math.pow(
          1 +
            annualRate / 100 / periodConfig[nextPaymentPeriod].paymentsPerYear,
          periodConfig[nextPaymentPeriod].paymentsPerYear,
        ) -
          1) *
        100,
      remainingDebt,
      savedInterest,
    };
  };

  const handleCalculate = () => {
    setHasCalculated(true);
    setResult(calculateResult());
  };

  const calculateWithOverrides = (overrides: CalculationOverrides) => {
    setHasCalculated(true);
    setResult(calculateResult(overrides));
  };

  const handleMoneyChange =
    (setter: (value: string) => void) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = parseNumber(event.target.value);
      setter(rawValue ? rawValue.toLocaleString("en-US") : "");
    };

  const resetResult = () => {
    if (hasCalculated) {
      setHasCalculated(false);
      setResult(null);
    }
  };

  return (
    <div className="relative w-full rounded-2xl bg-linear-to-b from-yellow-500 to-transparent p-[2px] shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:rounded-3xl">
      <div className="grid size-full items-stretch gap-5 rounded-[18px] bg-white p-3 sm:gap-6 sm:rounded-[22px] sm:p-4 lg:grid-cols-2 lg:p-6">
        <section className="order-1 flex h-full flex-col">
          <div className="border-primary-main/15 bg-primary-main/[0.03] mb-4 flex items-center gap-3 rounded-2xl border p-3 sm:p-4">
            <span className="bg-primary-main/10 text-primary-main flex size-10 shrink-0 items-center justify-center rounded-2xl sm:size-12">
              <BdCalculator size={30} />
            </span>
            <div className="min-w-0">
              <h2 className="text-base leading-8 font-bold text-slate-900 sm:text-xl">
                محاسبه اقساط وام
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                رایگان · آنلاین · بدون ثبت‌نام
              </p>
            </div>
          </div>

          <div className="border-primary-main/15 mb-4 rounded-2xl border p-4">
            <h3 className="font-bold text-slate-800">
              {selectedScenario?.label}
            </h3>
            <p className="mt-2 text-sm leading-7 text-slate-500">
              {selectedScenario?.description}
            </p>
          </div>

          <div className="mb-3 grid grid-cols-1 gap-2 min-[360px]:grid-cols-2 sm:grid-cols-5">
            {scenarios.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  setScenario(item.key);
                  resetResult();
                }}
                className={cn(
                  "border-primary-main/15 hover:border-primary-main/35 min-h-12 rounded-xl border bg-white px-3 py-3 text-sm font-bold text-slate-700 transition",
                  scenario === item.key &&
                    "bg-primary-main text-white shadow-[0_10px_24px_rgba(65,56,168,0.25)]",
                )}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="mb-4 grid grid-cols-1 gap-2 min-[520px]:grid-cols-3">
            {scenarioPresets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  const nextAmount = preset.amount.toLocaleString("en-US");
                  const nextRate = preset.rate ? String(preset.rate) : "";
                  const nextMonths = String(preset.months);
                  const nextKnownInstallment = preset.knownInstallment
                    ? preset.knownInstallment.toLocaleString("en-US")
                    : knownInstallment;
                  const nextPaidInstallments = preset.paidInstallments
                    ? String(preset.paidInstallments)
                    : paidInstallments;
                  const nextGrowthRate = preset.growthRate
                    ? String(preset.growthRate)
                    : growthRate;

                  setAmount(nextAmount);
                  setRate(nextRate);
                  setMonths(nextMonths);

                  if (preset.knownInstallment) {
                    setKnownInstallment(nextKnownInstallment);
                  }

                  if (preset.paidInstallments) {
                    setPaidInstallments(nextPaidInstallments);
                  }

                  if (preset.growthRate) {
                    setGrowthRate(nextGrowthRate);
                  }

                  calculateWithOverrides({
                    amount: nextAmount,
                    rate: nextRate,
                    months: nextMonths,
                    knownInstallment: nextKnownInstallment,
                    paidInstallments: nextPaidInstallments,
                    growthRate: nextGrowthRate,
                  });
                }}
                className="border-primary-main/15 text-primary-main hover:border-primary-main/35 hover:bg-primary-main/[0.03] min-h-12 rounded-xl border px-3 py-3 text-sm leading-6 font-bold transition"
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="grid gap-4 min-[520px]:grid-cols-2">
            <InputBlock
              label="مبلغ وام (تومان)"
              value={amount}
              onChange={(event) => {
                handleMoneyChange(setAmount)(event);
                resetResult();
              }}
              placeholder="مثلا 100,000,000"
            />
            {scenario === "rate" ? (
              <InputBlock
                label="مبلغ هر قسط (تومان)"
                value={knownInstallment}
                onChange={(event) => {
                  handleMoneyChange(setKnownInstallment)(event);
                  resetResult();
                }}
                placeholder="مثلا 3,870,000"
              />
            ) : (
              <InputBlock
                label={
                  scenario === "qard"
                    ? "کارمزد سالانه (%)"
                    : "نرخ سود سالانه (%)"
                }
                value={rate}
                onChange={(event) => {
                  setRate(toEnglishDigits(event.target.value));
                  resetResult();
                }}
                placeholder="مثلا 23"
              />
            )}
            <InputBlock
              label={scenario === "qard" ? "تعداد اقساط ماهانه" : "تعداد اقساط"}
              value={months}
              onChange={(event) => {
                setMonths(toEnglishDigits(event.target.value));
                resetResult();
              }}
              placeholder="مثلا 36"
            />
            {scenario === "early" ? (
              <InputBlock
                label="تعداد اقساط پرداخت‌شده"
                value={paidInstallments}
                onChange={(event) => {
                  setPaidInstallments(toEnglishDigits(event.target.value));
                  resetResult();
                }}
                placeholder="مثلا 12"
              />
            ) : scenario === "step" ? (
              <InputBlock
                label="درصد رشد سالانه قسط (%)"
                value={growthRate}
                onChange={(event) => {
                  setGrowthRate(toEnglishDigits(event.target.value));
                  resetResult();
                }}
                placeholder="مثلا 10"
              />
            ) : (
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  دوره پرداخت
                </label>
                <select
                  value={paymentPeriod}
                  onChange={(event) => {
                    setPaymentPeriod(event.target.value as PaymentPeriod);
                    resetResult();
                  }}
                  className="border-primary-main/10 focus:border-primary-main/45 h-14 w-full rounded-xl border bg-white px-4 text-sm font-bold text-slate-800 outline-none"
                >
                  {Object.entries(periodConfig).map(([key, item]) => (
                    <option key={key} value={key}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-2 sm:justify-start">
            {quickRates.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  const nextRate = String(item);
                  setRate(nextRate);
                  calculateWithOverrides({ rate: nextRate });
                }}
                className="border-primary-main/15 text-primary-main rounded-full border px-3 py-1 text-xs font-bold"
              >
                {item}٪
              </button>
            ))}
            {quickMonths.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  const nextMonths = String(item);
                  setMonths(nextMonths);
                  calculateWithOverrides({ months: nextMonths });
                }}
                className="border-primary-main/15 text-primary-main rounded-full border px-3 py-1 text-xs font-bold"
              >
                {item} ماه
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={handleCalculate}
            className="bg-primary-main mt-5 h-14 rounded-2xl text-base font-bold text-white shadow-[0_12px_28px_rgba(65,56,168,0.25)] transition hover:brightness-105"
          >
            محاسبه
          </button>

          <div className="bg-primary-main/[0.03] mt-5 rounded-2xl p-3 text-sm leading-7 text-slate-600 sm:mt-auto sm:p-4">
            محاسبات بر اساس فرمول اقساط مساوی انجام می‌شود و نتیجه جنبه راهنمایی
            دارد؛ مبلغ دقیق قسط را بانک عامل هنگام عقد قرارداد اعلام می‌کند.
          </div>
        </section>

        <section className="border-primary-main/20 bg-primary-main/[0.02] order-2 flex h-full min-w-0 flex-col rounded-2xl border border-dashed p-3 sm:p-4">
          <h3 className="mb-2 text-lg font-bold text-slate-800">
            خروجی وام همین‌جا ساخته می‌شود
          </h3>
          <p className="mb-5 text-sm leading-7 text-slate-600">
            اطلاعات وام را وارد کنید و دکمه محاسبه را بزنید تا قسط، سود کل، نرخ
            موثر و جدول بازپرداخت نمایش داده شود.
          </p>

          <div className="grid grid-cols-1 gap-3 min-[430px]:grid-cols-2">
            <SummaryCard label="قسط هر دوره" value={result?.installment} />
            <SummaryCard
              label="سود / کارمزد کل"
              value={result?.totalInterest}
            />
            <SummaryCard
              label="نرخ موثر"
              text={result ? formatPercent(result.effectiveRate) : "-"}
            />
            <SummaryCard label="مجموع پرداخت" value={result?.totalPayment} />
          </div>

          {hasCalculated && !result && (
            <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm leading-7 text-amber-800">
              برای محاسبه، مبلغ وام و تعداد اقساط را وارد کنید. در حالت «یافتن
              نرخ»، مبلغ هر قسط هم الزامی است.
            </div>
          )}

          {scenario === "early" && result && (
            <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-800">
              مانده تقریبی برای تسویه:{" "}
              <strong>{formatNumber(result.remainingDebt)} تومان</strong>
              <br />
              سود قابل صرفه‌جویی:{" "}
              <strong>{formatNumber(result.savedInterest)} تومان</strong>
            </div>
          )}

          <div className="mt-5 min-h-[310px] flex-1 overflow-x-auto overflow-y-auto rounded-2xl border border-slate-100 bg-white">
            {result ? (
              <table className="w-full min-w-[540px] text-right text-xs">
                <thead className="sticky top-0 bg-slate-50 text-slate-600">
                  <tr>
                    <th className="p-3">قسط</th>
                    <th className="p-3">مبلغ قسط</th>
                    <th className="p-3">اصل</th>
                    <th className="p-3">سود</th>
                    <th className="p-3">مانده</th>
                  </tr>
                </thead>
                <tbody>
                  {result.schedule.slice(0, 120).map((row) => (
                    <tr key={row.period} className="border-t border-slate-100">
                      <td className="p-3 font-bold">{row.period}</td>
                      <td className="p-3">{formatNumber(row.installment)}</td>
                      <td className="p-3">{formatNumber(row.principal)}</td>
                      <td className="p-3">{formatNumber(row.interest)}</td>
                      <td className="p-3">{formatNumber(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex h-full min-h-[310px] items-center justify-center p-6 text-center text-sm leading-7 text-slate-500">
                نتیجه پس از وارد کردن اطلاعات و زدن دکمه محاسبه در این بخش نمایش
                داده می‌شود.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

const InputBlock = ({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
}) => (
  <div>
    <label className="mb-2 block text-sm font-bold text-slate-700">
      {label}
    </label>
    <div className="border-primary-main/10 focus-within:border-primary-main/45 h-14 w-full rounded-xl border bg-white px-4 transition">
      <input
        value={value}
        onChange={onChange}
        inputMode="numeric"
        placeholder={placeholder}
        className="size-full border-0 bg-transparent text-left text-sm font-bold text-slate-800 outline-none"
        dir="ltr"
      />
    </div>
  </div>
);

const SummaryCard = ({
  label,
  value,
  text,
}: {
  label: string;
  value?: number;
  text?: string;
}) => (
  <div className="border-primary-main/10 rounded-xl border bg-white p-3 text-center">
    <p className="text-primary-main text-sm font-bold">{label}</p>
    <p className="mt-2 text-lg font-black text-slate-900">
      {text ?? (value ? `${formatNumber(value)} تومان` : "-")}
    </p>
  </div>
);

export default LoanInstallmentCalculator;
